import Endpoint from "./endpoint.js";
import Zajax from "./zajax.js";
import createImageAttachmentDisplay from "./components/image_attachment_display.js";
import createVideoAttachmentDisplay from "./components/video_attachment_display.js";
import createAudioAttachmentDisplay from "./components/audio_attachment_display.js";
import createFileAttachmentDisplay from "./components/file_attachment_display.js";
import { createElement } from "./components/dom_utils.js";
import { createDownloadButton } from "./components/download_button.js";

let selectedAttachments = [];
let uploadedAttachments = [];

const attachmentStyles = `
  #livechat_widget .widget_attachments_container {
    display: flex !important;
    flex-direction: row !important;
    gap: 8px !important;
    padding: 8px 0 !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
  }
  #livechat_widget .widget_attachments_container::-webkit-scrollbar {
    display: none !important;
  }
  #livechat_widget .widget_attachment_item {
    position: relative !important;
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    gap: 8px !important;
    padding: 8px !important;
    border-radius: 8px !important;
    border: 1px solid rgba(0, 0, 0, 0.08) !important;
    background: transparent !important;
    min-width: 150px !important;
    flex-shrink: 0 !important;
    transition: background-color 0.3s ease !important;
    cursor: pointer !important;
  }
  #livechat_widget #widget_attachments_display .widget_attachment_item {
    max-width: 200px !important;
  }
  #livechat_widget .widget_attachment_item:hover {
    background: rgba(0, 0, 0, 0.04) !important;
  }
  #livechat_widget .widget_attachment_preview_item {
    justify-content: space-between !important;
  }
  #livechat_widget .widget_attachment_icon {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    min-height: 32px !important;
    border-radius: 6px !important;
    border: 1px solid rgba(0, 0, 0, 0.08) !important;
    background: rgba(0, 0, 0, 0.04) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    overflow: hidden !important;
    flex-shrink: 0 !important;
  }
  #livechat_widget .widget_attachment_icon img {
    width: 20px !important;
    height: 20px !important;
    object-fit: contain !important;
  }
  #livechat_widget .widget_attachment_name {
    font-size: 13px !important;
    color: #111 !important;
    font-family: "Plus Jakarta", sans-serif !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    flex: 1 !important;
    min-width: 0 !important;
  }
  #livechat_widget .widget_attachment_remove {
    position: absolute !important;
    top: -8px !important;
    right: -8px !important;
    width: 18px !important;
    height: 18px !important;
    border-radius: 50% !important;
    background: #888 !important;
    border: 2px solid white !important;
    display: none !important;
    align-items: center !important;
    justify-content: center !important;
    cursor: pointer !important;
    z-index: 10 !important;
  }
  #livechat_widget .widget_attachment_item:hover .widget_attachment_remove {
    display: flex !important;
  }
  #livechat_widget .widget_attachment_remove svg {
    width: 8px !important;
    height: 8px !important;
  }
  #livechat_widget .widget_attach_button {
    cursor: pointer !important;
    width: 30px !important;
    height: 30px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    opacity: 0.7 !important;
    transition: opacity 0.2s ease !important;
  }
  #livechat_widget .widget_attach_button:hover {
    opacity: 1 !important;
  }
  #livechat_widget .widget_attachment_actions {
    flex-shrink: 0 !important;
  }
  #livechat_widget .widget_attachment_action_btn:hover {
    background: rgba(0, 0, 0, 0.05) !important;
    border-radius: 4px !important;
  }
  @keyframes widget-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function injectStyles() {
  if (!document.getElementById("livechat_attachment_styles")) {
    const styleElement = document.createElement("style");
    styleElement.id = "livechat_attachment_styles";
    styleElement.textContent = attachmentStyles;
    document.head.appendChild(styleElement);
  }
}

const LivechatAttachments = {
  init() {
    injectStyles();
  },

  createLoader(size = 16) {
    return createElement("div", {
      style: `
        width: ${size}px !important;
        height: ${size}px !important;
        border: 2px solid rgba(0, 0, 0, 0.1) !important;
        border-top-color: rgba(0, 0, 0, 0.6) !important;
        border-radius: 50% !important;
        animation: widget-spin 0.8s linear infinite !important;
      `,
    });
  },

  getFileIcon(fileName) {
    const extension = fileName.split(".").pop().toLowerCase();
    const iconMap = {
      pdf: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/pdf_file.svg",
      doc: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/doc_file.svg",
      docx: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/doc_file.svg",
      ppt: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/ppt_file.svg",
      pptx: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/ppt_file.svg",
      csv: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/csv_file.svg",
      xls: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/csv_file.svg",
      xlsx: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/csv_file.svg",
      mp4: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/video_file.svg",
      mov: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/video_file.svg",
      avi: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/video_file.svg",
      mkv: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/video_file.svg",
      webm: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/video_file.svg",
      mp3: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/audio_file.svg",
      wav: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/audio_file.svg",
      ogg: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/audio_file.svg",
      m4a: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/audio_file.svg",
      jpg: null,
      jpeg: null,
      png: null,
      gif: null,
      webp: null,
      svg: "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/default_file.svg",
    };
    return (
      iconMap[extension] ||
      "https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/inbound_agents/default_file.svg"
    );
  },

  isImage(fileName) {
    const extension = fileName.split(".").pop().toLowerCase();
    return ["jpg", "jpeg", "png", "gif", "webp"].includes(extension);
  },

  isVideo(fileName) {
    const extension = fileName.split(".").pop().toLowerCase();
    return ["mp4", "mov", "avi", "mkv", "webm"].includes(extension);
  },

  isAudio(fileName) {
    const extension = fileName.split(".").pop().toLowerCase();
    return ["mp3", "wav", "ogg", "m4a"].includes(extension);
  },

  getAttachmentDisplayType(fileName) {
    if (this.isImage(fileName)) return "image";
    if (this.isVideo(fileName)) return "video";
    if (this.isAudio(fileName)) return "audio";
    return "file";
  },

  handleFileSelection(files, workspaceId) {
    files.forEach((file) => {
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const attachment = {
        id: tempId,
        file: file,
        file_name: file.name,
        isLoading: true,
        isUploaded: false,
      };
      selectedAttachments.push(attachment);
      this.renderAttachment(attachment);
    });

    this.uploadAttachments(workspaceId);
  },

  uploadAttachments(workspaceId) {
    const pendingAttachments = selectedAttachments.filter(
      (a) => !a.isUploaded && !a.uploadError,
    );
    if (pendingAttachments.length === 0) return;

    const formData = new FormData();
    pendingAttachments.forEach((attachment) => {
      formData.append("files", attachment.file);
    });

    Zajax.multipartPost(
      Endpoint.apiUrl() + "livechat/attachments/upload",
      formData,
      {
        "X-Workspace-Id": workspaceId.toString(),
      },
      (data) => {
        if (data.success && data.files) {
          data.files.forEach((uploadedFile, index) => {
            const attachment = pendingAttachments[index];
            if (attachment) {
              const oldId = attachment.id;
              attachment.isLoading = false;
              attachment.isUploaded = true;
              attachment.id = uploadedFile.id;
              attachment.url = uploadedFile.url;
              attachment.file_name = uploadedFile.file_name;
              attachment.content_type = uploadedFile.content_type;
              uploadedAttachments.push({
                id: uploadedFile.id,
                file_name: uploadedFile.file_name,
                url: uploadedFile.url,
                content_type: uploadedFile.content_type,
              });
              LivechatAttachments.updateAttachmentUI(attachment, oldId);
            }
          });
        } else {
          pendingAttachments.forEach((attachment) => {
            attachment.isLoading = false;
            attachment.uploadError = true;
            LivechatAttachments.updateAttachmentUI(attachment, attachment.id);
          });
        }
      },
      () => {
        pendingAttachments.forEach((attachment) => {
          attachment.isLoading = false;
          attachment.uploadError = true;
          LivechatAttachments.updateAttachmentUI(attachment, attachment.id);
        });
      },
    );
  },

  renderAttachment(attachment) {
    const container = document.getElementById("widget_attachments_display");
    if (!container) return;

    container.style.setProperty("display", "flex", "important");

    const attachmentItem = createElement("div", {
      className: "widget_attachment_item",
      "data-attachment-id": attachment.id,
    });

    const iconContainer = createElement("div", {
      className: "widget_attachment_icon",
    });

    if (this.isImage(attachment.file_name) && attachment.file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        iconContainer.innerHTML = `<img src="${e.target.result}" alt="${attachment.file_name}" style="width: 100% !important; height: 100% !important; object-fit: cover !important;" />`;
      };
      reader.readAsDataURL(attachment.file);
    } else {
      const iconUrl = this.getFileIcon(attachment.file_name);
      iconContainer.innerHTML = `<img src="${iconUrl}" alt="file icon" />`;
    }

    const nameSpan = createElement(
      "span",
      {
        className: "widget_attachment_name",
      },
      attachment.file_name,
    );

    const loaderOrRemove = createElement("div", {
      className: "widget_attachment_remove",
    });

    if (attachment.isLoading) {
      loaderOrRemove.appendChild(this.createLoader());
      loaderOrRemove.style.setProperty("display", "flex", "important");
      loaderOrRemove.style.setProperty("background", "none", "important");
    } else {
      loaderOrRemove.innerHTML = `<svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 1L1 7M1 1L7 7" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
      loaderOrRemove.style.cursor = "pointer";
      loaderOrRemove.onclick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        LivechatAttachments.removeAttachment(attachment.id);
      };
    }

    attachmentItem.appendChild(iconContainer);
    attachmentItem.appendChild(nameSpan);
    attachmentItem.appendChild(loaderOrRemove);
    container.appendChild(attachmentItem);
  },

  updateAttachmentUI(attachment, searchId = null) {
    const idToSearch = searchId || attachment.id;
    const attachmentItem = document.querySelector(
      `[data-attachment-id="${idToSearch}"]`,
    );
    if (!attachmentItem) return;

    if (searchId && searchId !== attachment.id) {
      attachmentItem.setAttribute("data-attachment-id", attachment.id);
    }

    if (attachment.uploadError) {
      attachmentItem.style.setProperty("border-color", "#ff4d4f", "important");
      attachmentItem.style.setProperty("opacity", "0.6", "important");
    }

    if (!attachment.isLoading && attachment.isUploaded) {
      const loaderOrRemove = attachmentItem.querySelector(
        ".widget_attachment_remove",
      );
      if (loaderOrRemove) {
        loaderOrRemove.innerHTML = `<svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7 1L1 7M1 1L7 7" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;
        loaderOrRemove.style.cursor = "pointer";
        loaderOrRemove.style.removeProperty("display");
        loaderOrRemove.style.setProperty("background", "#888", "important");
        loaderOrRemove.onclick = (e) => {
          e.stopPropagation();
          e.preventDefault();
          LivechatAttachments.removeAttachment(attachment.id);
        };
      }
    }
  },

  removeAttachment(attachmentId) {
    const index = selectedAttachments.findIndex((a) => a.id === attachmentId);
    if (index > -1) {
      selectedAttachments.splice(index, 1);
    }

    const uploadedIndex = uploadedAttachments.findIndex(
      (a) => a.id === attachmentId,
    );
    if (uploadedIndex > -1) {
      uploadedAttachments.splice(uploadedIndex, 1);
    }

    const attachmentItem = document.querySelector(
      `[data-attachment-id="${attachmentId}"]`,
    );
    if (attachmentItem) {
      attachmentItem.remove();
    }

    const container = document.getElementById("widget_attachments_display");
    if (container && selectedAttachments.length === 0) {
      container.style.setProperty("display", "none", "important");
    }
  },

  clearAttachments() {
    selectedAttachments = [];
    uploadedAttachments = [];
    const container = document.getElementById("widget_attachments_display");
    if (container) {
      container.innerHTML = "";
      container.style.setProperty("display", "none", "important");
    }
  },

  getUploadedAttachments() {
    return uploadedAttachments;
  },

  createAttachmentsDisplay(attachments) {
    const container = createElement("div", {
      style:
        "display: flex; flex-direction: column; gap: 6px; width: 80%; align-items: stretch;",
    });

    attachments.forEach((attachment) => {
      const attachmentItem = createElement("div", {
        className: "widget_attachment_item widget_attachment_preview_item",
      });

      const contentWrapper = createElement("div", {
        className: "widget_attachment_content",
        style:
          "display: flex; flex-direction: row; align-items: center; gap: 8px; flex: 1; min-width: 0;",
      });

      const displayType = this.getAttachmentDisplayType(attachment.file_name);
      let displayComponent;

      switch (displayType) {
        case "image":
          displayComponent = createImageAttachmentDisplay(attachment);
          break;
        case "video":
          displayComponent = createVideoAttachmentDisplay(attachment);
          break;
        case "audio":
          displayComponent = createAudioAttachmentDisplay(attachment);
          contentWrapper.appendChild(displayComponent);
          container.appendChild(attachmentItem);
          attachmentItem.appendChild(contentWrapper);
          return;
        case "file":
        default:
          displayComponent = createFileAttachmentDisplay(attachment);
          break;
      }

      const nameSpan = createElement(
        "span",
        {
          className: "widget_attachment_name",
        },
        attachment.file_name,
      );

      contentWrapper.appendChild(displayComponent);
      contentWrapper.appendChild(nameSpan);

      const actionsContainer = createElement("div", {
        className: "widget_attachment_actions",
        style:
          "display: none; flex-direction: row; align-items: center; gap: 6px; padding: 4px 6px; border-radius: 6px; border: 1px solid rgba(0, 0, 0, 0.08); background: rgba(255, 255, 255, 0.5);",
      });

      const downloadButton = createDownloadButton({
        attachment,
        styles: {
          width: 28,
          height: 28,
          iconWidth: 16,
          iconHeight: 16,
          showByDefault: true,
        },
      });

      actionsContainer.appendChild(downloadButton);

      attachmentItem.appendChild(contentWrapper);
      attachmentItem.appendChild(actionsContainer);

      attachmentItem.addEventListener("mouseenter", () => {
        actionsContainer.style.display = "flex";
      });

      attachmentItem.addEventListener("mouseleave", () => {
        actionsContainer.style.display = "none";
      });

      container.appendChild(attachmentItem);
    });

    return container;
  },
};

LivechatAttachments.init();

export default LivechatAttachments;